import React from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  CANDIDATE_STATUS_LABEL,
  CANDIDATE_STATUS_ORDER,
  CandidateRowStatus,
  ImportProgress,
} from './candidate-status.helpers';
import { STATUS_COLOR, STATUS_ICON_SIDEBAR } from './candidate-status-display';
import { formatImportProgressLabel } from './import-display.helpers';

const useStyles = makeStyles()((theme) => ({
  track: {
    borderRadius: 4,
    display: 'flex',
    height: 10,
    overflow: 'hidden',
    width: '100%',
    backgroundColor: 'rgba(0,0,0,0.06)',
  },
  segment: {
    height: '100%',
    minWidth: 2,
    transition: 'width 0.2s ease-out',
  },
  summary: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontVariantNumeric: 'tabular-nums',
    marginTop: theme.spacing(1),
  },
  legend: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1.5),
  },
  legendRow: {
    alignItems: 'center',
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    columnGap: theme.spacing(2),
  },
  legendLabel: {
    alignItems: 'center',
    display: 'flex',
    fontSize: 13,
    gap: theme.spacing(0.75),
  },
  legendValue: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
  },
}));

type ImportStatusChartProps = {
  counts: Partial<Record<CandidateRowStatus, number>>;
  progress: ImportProgress;
};

/** Stacked bar + legend for candidate-row status breakdown in the import side panel. */
export const ImportStatusChart: React.FC<ImportStatusChartProps> = ({
  counts,
  progress,
}) => {
  const { classes } = useStyles();
  const { total, resolved } = progress;
  const segments = CANDIDATE_STATUS_ORDER.filter((s) => (counts[s] ?? 0) > 0);

  const summary =
    total === 0 ? 'No rows' : formatImportProgressLabel(resolved, total);

  return (
    <>
      <Box className={classes.track} aria-hidden={total === 0}>
        {segments.map((status) => {
          const count = counts[status] ?? 0;
          const widthPercent = total === 0 ? 0 : (count / total) * 100;
          return (
            <Box
              key={status}
              className={classes.segment}
              sx={{
                width: `${widthPercent}%`,
                backgroundColor: STATUS_COLOR[status],
              }}
              title={`${CANDIDATE_STATUS_LABEL[status]}: ${count}`}
            />
          );
        })}
      </Box>
      <Typography className={classes.summary}>{summary}</Typography>
      {segments.length > 0 && (
        <Box className={classes.legend}>
          {CANDIDATE_STATUS_ORDER.map((status) => {
            const count = counts[status] ?? 0;
            if (!count) return null;
            return (
              <Box key={status} className={classes.legendRow}>
                <span className={classes.legendLabel}>
                  <Box
                    component="span"
                    sx={{
                      alignItems: 'center',
                      color: STATUS_COLOR[status],
                      display: 'inline-flex',
                      flexShrink: 0,
                      justifyContent: 'center',
                    }}
                  >
                    {STATUS_ICON_SIDEBAR[status]}
                  </Box>
                  {CANDIDATE_STATUS_LABEL[status]}
                </span>
                <span className={classes.legendValue}>{count}</span>
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
};
